import React from "react";
import LegalLayout from "../components/legal/LegalLayout";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";

export default function Terms() {
  const seo = getPageSeo("/terms");
  return (
    <LegalLayout title="Terms of Service" updated="August 25, 2026">
      <SEO title={seo.title} description={seo.description} path="/terms" />
      <section>
        <p>
          These Terms of Service ("Terms") govern your access to and use of ServiceGuru Pro (the "Service"),
          operated by ServiceGuru Pro, a business registered in the State of Florida (Registration No.
          G26000098018). By creating an account or using the Service, you agree to these Terms. If you are entering
          into these Terms on behalf of a business, you represent that you have authority to bind that business.
        </p>
      </section>

      <section>
        <h2>1. Description of Service</h2>
        <p>
          ServiceGuru Pro is a business-to-business (B2B) software-as-a-service ("SaaS") CRM platform designed for
          field service businesses (such as HVAC, plumbing, electrical, and similar trades). The Service provides
          job scheduling and dispatch, estimates and invoicing, customer management, staff and payroll tools,
          marketing and pipeline tools, and AI-powered features including a voice agent and diagnostic assistant.
        </p>
      </section>

      <section>
        <h2>2. Acceptable Use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Violate any applicable law or regulation, including telemarketing, SMS, and do-not-call rules;</li>
          <li>Send unsolicited, deceptive, or unlawful communications to any person who has not consented to receive them;</li>
          <li>Transmit malware, spam, or engage in any activity that disrupts or compromises the Service or its infrastructure;</li>
          <li>Attempt to access another company's data or accounts without authorization; or</li>
          <li>Reverse-engineer, resell, or sublicense the Service without our prior written consent.</li>
        </ul>
        <p>We may suspend or terminate accounts that violate this Acceptable Use policy.</p>
      </section>

      <section>
        <h2>3. Subscriptions &amp; Billing</h2>
        <p>
          Access to the Service requires an active paid subscription following any applicable free trial period.
          Subscription fees are billed in advance on a recurring basis (monthly or annually, as selected at sign-up)
          via our payment processor, Stripe. Subscriptions renew automatically unless cancelled before the next
          billing date. Except as required by law or expressly stated otherwise, subscription fees are
          non-refundable, including for partial billing periods.
        </p>
      </section>

      <section>
        <h2>4. AI Credits</h2>
        <p>
          Certain AI-powered features (including the AI voice agent, AI follow-up calls, and diagnostic assistant)
          consume "AI credits" from your account's credit balance, based on usage. AI credits may be included with
          your subscription plan and/or purchased separately. <strong>AI credits that have already been consumed
          are non-refundable.</strong> Unused AI credit balances are not redeemable for cash and, unless otherwise
          stated at the time of purchase, do not carry over past subscription cancellation or the stated expiration
          period.
        </p>
      </section>

      <section>
        <h2>5. Your Responsibilities</h2>
        <p>As a user of the Service, you are responsible for:</p>
        <ul>
          <li>The accuracy of information you enter, including customer contact details used for SMS, voice, and email communications;</li>
          <li>Obtaining any consent required from your own customers before contacting them through the Service;</li>
          <li>Maintaining the confidentiality of your account credentials; and</li>
          <li>All activity that occurs under your account, including actions taken by staff you invite.</li>
        </ul>
      </section>

      <section>
        <h2>6. SMS &amp; Voice Communications (A2P 10DLC Compliance)</h2>
        <p>
          By providing a phone number to the Service (as an account holder, staff member, or by collecting a
          customer's phone number for use in the Service), you and/or your customers consent to receive text
          messages and automated voice calls related to field service operations, including:
        </p>
        <ul>
          <li>Job updates and status notifications;</li>
          <li>Appointment confirmations and reminders;</li>
          <li>Estimates and invoices; and</li>
          <li>AI-powered follow-up calls regarding open estimates, invoices, or scheduling.</li>
        </ul>
        <p>
          <strong>Message frequency</strong> varies based on account activity and may be multiple messages per
          week. <strong>Message and data rates may apply.</strong> Recipients may opt out of SMS messages at any
          time by replying <strong>STOP</strong> to any message. It is your responsibility as a business using the
          Service to ensure you have proper consent from your own customers before sending them messages or calls
          through the Service, and to honor opt-out requests promptly.
        </p>
      </section>

      <section>
        <h2>7. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, ServiceGuru Pro and its officers, employees, and affiliates will
          not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of
          profits, revenue, data, or business opportunity, arising out of or related to your use of the Service,
          even if advised of the possibility of such damages. Our total liability for any claim arising from these
          Terms or the Service will not exceed the amount you paid us in the twelve (12) months preceding the claim.
          The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
        </p>
      </section>

      <section>
        <h2>8. Termination</h2>
        <p>
          You may cancel your subscription at any time through your account settings; cancellation takes effect at
          the end of the current billing period. We may suspend or terminate your access to the Service, with or
          without notice, if you breach these Terms, fail to pay applicable fees, or engage in conduct that we
          reasonably believe harms the Service, other users, or third parties. Upon termination, your right to use
          the Service ends immediately; certain provisions of these Terms (including billing, AI credits, and
          limitation of liability) survive termination.
        </p>
      </section>

      <section>
        <h2>9. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the State of Florida, without regard to its conflict-of-law
          principles. Any dispute arising out of or relating to these Terms or the Service will be subject to the
          exclusive jurisdiction of the state and federal courts located in Florida.
        </p>
      </section>

      <section>
        <h2>10. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. We will post the updated version on this page with a revised
          "Last updated" date. Continued use of the Service after changes take effect constitutes acceptance of the
          revised Terms.
        </p>
      </section>

      <section>
        <h2>11. Contact Us</h2>
        <p>
          For questions about these Terms or any legal notices, contact us at{" "}
          <a href="mailto:info@servicegurupro.com">info@servicegurupro.com</a>.
        </p>
        <p className="text-slate-500 text-[13px]">
          ServiceGuru Pro is a business entity registered in the State of Florida, Registration No. G26000098018.
        </p>
      </section>
    </LegalLayout>
  );
}
